import { ButtonStyle, ButtonVariant, ColorScheme, TextStyle } from 'scripts/type'
import { defaultButtonStyle, defaultColorScheme, defaultTextStyle } from 'scripts/constants'
import { getIsLightColor, getRgbColor } from './colorUtils'

const grayColor = '#212529'
const whiteColor = '#ffffff'

type Params = {
  colorScheme?: ColorScheme
  buttonStyle: ButtonStyle
  textStyle: TextStyle
}

export default function getRootStyle(params: Params) {
  const { colorScheme = defaultColorScheme, buttonStyle = defaultButtonStyle, textStyle = defaultTextStyle } = params
  const { accent, background } = colorScheme

  const {
    textColor,
    textSecondaryColor,
    textPlaceholderColor,
    brandingTextColor,
    textDescriptionColor,
    borderColor,
    textCloseColor,
    brandingBgHoverColor,
    closeBgHoverColor,
    previewBgColor,
    previewBorderColor,
  } = calcColorByBackground(background)

  const isFilledButton = buttonStyle.variant === ButtonVariant.FILLED
  const accentRgb = getRgbColor(accent)
  const buttonStyles = isFilledButton
    ? getFilledButtonStyle(accent, background)
    : getOutlineButtonStyle(accent, background, textColor)

  return `
    --primary-color: ${accent};
    --primary-color-rgb: ${accentRgb};
    --background-color: ${background};
    --border-color: ${borderColor};
    --text-color: ${textColor};
    --text-on-primary-color: ${whiteColor};
    --text-description-color: ${textDescriptionColor};
    --text-secondary-color: ${textSecondaryColor};
    --text-placeholder-color: ${textPlaceholderColor};
    --close-btn-text-color: ${textCloseColor};
    --close-btn-background-hover-color: ${closeBgHoverColor};
    --branding-text-color: ${brandingTextColor};
    --branding-background-color-hover: ${brandingBgHoverColor};
    --transition: 0.3s cubic-bezier(0.5, 1, 0.89, 1);
    --border-radius: 6px;
    --title-text-align: ${textStyle.align.toLowerCase()};
    --button-radius: ${buttonStyle.borderRadius}px;
    --preview-background-color: ${previewBgColor};
    --preview-border-color: ${previewBorderColor};
    ${buttonStyles}
  `
}

function getDisabledFilledButtonColors(background: string) {
  const textColorRgb = getRgbColor(whiteColor)

  return getIsLightColor(background)
    ? {
        bg: '#D4DAE0',
        border: '#D4DAE0',
        text: whiteColor,
      }
    : {
        bg: `rgba(${textColorRgb}, 0.5)`,
        border: `rgba(${textColorRgb}, 0.5)`,
        text: `rgba(${textColorRgb}, 0.3)`,
      }
}

function getFilledButtonStyle(accent: string, background: string) {
  const accentColorRgb = getRgbColor(accent)
  const { bg, border, text } = getDisabledFilledButtonColors(background)

  return `
    --button-text-color: ${whiteColor};
    --button-background-color: ${accent};
    --button-border-color: ${accent};
    --button-background-color-hover: rgba(${accentColorRgb}, 0.85);
    --button-border-color-hover: transparent;
    --button-disabled-background-color: ${bg};
    --button-disabled-border-color: ${border};
    --button-disabled-text-color: ${text};
  `
}

function getOutlineButtonStyle(accent: string, background: string, textColor: string) {
  const accentColorRgb = getRgbColor(accent)
  const bgColorRgb = getRgbColor(background)
  const textColorRgb = getRgbColor(textColor)

  return `
    --button-text-color: ${accent};
    --button-background-color: ${whiteColor};
    --button-border-color: ${accent};
    --button-background-color-hover: ${whiteColor};
    --button-border-color-hover: rgba(${accentColorRgb}, 0.85);
    --button-disabled-background-color: rgba(${bgColorRgb}, 0.2);
    --button-disabled-border-color: rgba(${textColorRgb}, 0.5);
    --button-disabled-text-color: rgba(${textColorRgb}, 0.3);
  `
}

const colorWhenWhiteBgMap = {
  textColor: grayColor,
  textSecondaryColor: '#4d4f52',
  textPlaceholderColor: '#C0C6CC',
  textCloseColor: '#868E96',
  textDescriptionColor: '#7D8185',
  borderColor: '#E0E3E5',
  brandingTextColor: '#868E96',
  brandingBgHoverColor: '#F1F3F5',
  closeBgHoverColor: '#E9ECEF',
  previewBgColor: '#EDF2FC',
  previewBorderColor: '#3662D7',
}

function getColorsWhenCustomizedBg(background: string) {
  const textColorRgb = getRgbColor(whiteColor)
  return {
    textColor: whiteColor,
    textSecondaryColor: `rgba(${textColorRgb}, .8)`,
    textPlaceholderColor: `rgba(${textColorRgb}, .5)`,
    textCloseColor: `rgba(${textColorRgb}, .5)`,
    closeBgHoverColor: `rgba(${getRgbColor(background)}, .5)`,
    textDescriptionColor: `rgba(${textColorRgb}, .8)`,
    borderColor: `rgba(${textColorRgb}, .5)`,
    brandingTextColor: `rgba(${textColorRgb}, .5)`,
    brandingBgHoverColor: `rgba(${getRgbColor(background)}, .5)`,
    previewBgColor: `rgba(${textColorRgb}, .1)`,
    previewBorderColor: whiteColor,
  }
}

function calcColorByBackground(background: string) {
  return getIsLightColor(background) ? colorWhenWhiteBgMap : getColorsWhenCustomizedBg(background)
}
